/* eslint-disable max-len */
import { Variants } from 'framer-motion';
import React, { useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { MyBoardData } from '../../../api/endpoints/boards';
import { LabelValue } from '../../../platform/types/LabelValue';
import { Button, Input } from '../../../ui';
import { SortMode } from '../../common/Sorter';
import { SortModeSelector } from '../../workspace/SortModeSelector';
import { EmptyBlock } from '../elements/EmptyBlock';
import { Flexer } from '../elements/LayoutElements';
import { SearchEmpty } from '../templates/gallery/SearchEmpty';
import { useInfiniteBoardData } from './BoardHooks';
import { BoardListContext, BoardListItem, OnSelect } from './BoardListItem';

const sortOptions = [
    LabelValue('Last Visited', 'lastSeenAt'),
    LabelValue('Last Updated', 'updatedAt'),
    LabelValue('Date Created', 'createdAt'),
    LabelValue('Alphabetical', 'label'),
];

const BoardList = React.memo<{ boards: MyBoardData[] }>(({ boards }) =>
    <Flexer flex="row" wrap="wrap" gap="2">
        {boards.map(x => <BoardListItem key={x.id} board={x} />)}
    </Flexer>
);

const nextAnimation: Variants = {
    before: {
        opacity: 0,
    },
    in: {
        opacity: 1,
        y: '0rem',
        transition: {
            duration: .2,
            ease: 'easeOut',
        }
    },
    out: {
        opacity: 0,
        scale: 0.5,
        transition: {
            duration: .2,
            ease: 'easeOut',
        }
    }
};

export type SimpleBoardListViewProps = {
    workspaceId: string,
    onSelect?: OnSelect,
};

export const SimpleBoardListView = React.memo<SimpleBoardListViewProps>(({ workspaceId, onSelect }) => {

    const [search, setSearch] = useState('');
    const [sort, setSort] = useLocalStorage(`deqo/ui/${workspaceId}/sort`, ['lastSeenAt', 'desc'] as SortMode);

    const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteBoardData({}, search, sort);
    const allBoards = useMemo(() => (data?.pages?.flatMap(x => x.items) ?? []), [data]);

    if (!!allBoards.length || !!search) {
        return <BoardListContext.Provider value={{ onSelect, sort }}>
            <Flexer>
                <Flexer flex="row" gap="1">
                    <Flexer data-tour="ab-search" width="20">
                        <Input autoFocus placeholder="Search" value={search} onChange={setSearch} />
                    </Flexer>
                    <Flexer flex="row" width="12">
                        <SortModeSelector options={sortOptions} value={sort} onChange={setSort} />
                    </Flexer>
                </Flexer>
                {!!allBoards.length && <>
                    <Flexer flex="column" gap="2" padding="2 0">
                        <BoardList boards={allBoards} />
                    </Flexer>
                    {hasNextPage &&
                        <Flexer flex="row" center padding="4">
                            <Button onAction={() => fetchNextPage()}>Load More</Button>
                        </Flexer>
                    }
                </>}
                {!allBoards.length && <>
                    {!isLoading &&
                        <Flexer flex height="32" margin="2 0">
                            <SearchEmpty />
                        </Flexer>
                    }
                </>}
            </Flexer>
        </BoardListContext.Provider>;
    }
    else if (!isLoading) {
        return <EmptyBlock />;
    }

    return null;
});
